/**
 * Form05_ElectronicCommunicationsDisclosure.jsx
 * Collection: electronic_communications_disclosure
 * Redirect: /forms/06-fall-assessment
 */
import React, { useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { FormShell, Field, SigBlock, saveToMongo, downloadPDF, useToast, C } from "../shared/formKit";

const API_URL = "/api/forms/electronic-communications-disclosure";
const NEXT    = "/forms/06-fall-assessment";

export default function ElectronicCommunicationsDisclosure({ patientName="", onSubmit }) {
  const history = useHistory();
  const formRef  = useRef(null);
  const sigRef   = useRef(null);
  const [sigEmpty, setSigEmpty]     = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [savedId, setSavedId]       = useState(null);
  const [showToast, Toast]          = useToast();

  const [form, setForm] = useState({
    hasHardwareSoftware:   "",
    understoodRights:      false,
    understoodTypes:       false,
    understoodWithdrawal:  false,
    understoodMinReqs:     false,
    responsibleParty:      patientName,
    relationship:          "Self",
    sigDate:               new Date().toISOString().split("T")[0],
  });
  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const getSig = () => (!sigRef.current||sigRef.current.isEmpty()) ? null : sigRef.current.toDataURL("image/png");

  const buildDoc = () => ({
    formType: "electronic_communications_disclosure",
    patient:  { name: patientName },
    acknowledgments: {
      hasHardwareSoftware:  form.hasHardwareSoftware,
      understoodRights:     form.understoodRights,
      understoodTypes:      form.understoodTypes,
      understoodWithdrawal: form.understoodWithdrawal,
      understoodMinReqs:    form.understoodMinReqs,
    },
    authorization: {
      responsibleParty: form.responsibleParty,
      relationship:     form.relationship,
      sigDate:          form.sigDate || null,
      signatureDataURL: getSig(),
    },
    meta: { submittedAt: new Date().toISOString(), formVersion:"1.0.0" },
  });

  const handleSubmit = async () => {
    if (sigEmpty) { showToast("Please sign before submitting.","error"); return; }
    setSubmitting(true);
    try {
      const doc = buildDoc();
      const res = typeof onSubmit==="function" ? await onSubmit(doc) : await saveToMongo(doc, API_URL);
      if (res?._id) setSavedId(res._id);
      showToast("Saved! Redirecting…","success");
      setTimeout(()=>history.push(NEXT),1800);
    } catch(e){ console.error(e); showToast("Save failed.","error"); }
    finally { setSubmitting(false); }
  };

  const handleDownload = async () => {
    try { await downloadPDF(formRef,`ecommunications-${patientName||"patient"}.pdf`); showToast("Downloaded!"); }
    catch { showToast("PDF failed.","error"); }
  };

  const sections = [
    ["1","Your Legal Rights","Certain laws require us to provide specific information in writing. You have the right to receive that information on paper. We may provide it electronically if you consent. Your consent also applies to any other person named on your account."],
    ["2","Types of Electronic Communications","You agree we may provide agreements, disclosures, notices, account statements, policy updates, and health forms electronically via the Patient Portal, email, or other electronic means."],
    ["3","Setting Your Preferences","After consenting you will receive all Communications electronically. You may manage preferences under 'My Profile → Contact Preferences' in the Patient Portal."],
    ["4","Paper Communications","This Disclosure does not apply to communications we determine must be delivered in paper form under applicable law."],
    ["5","Minimum Requirements","Personal computer with 128-bit encryption-enabled browser, Internet access, valid email, and Adobe Reader 6.0+ for PDF communications."],
    ["6","Withdrawing Consent","You may withdraw consent by logging into the Patient Portal under 'My Profile → Contact Preferences', or by contacting your provider."],
    ["7","Obtaining Copies","Print or save Communications when first reviewed. Paper copies available within 12 months of original delivery; fees may apply."],
    ["8","Updating Contact Info","Notify us immediately of email/contact changes via the Patient Portal or by contacting your provider."],
    ["9","Retain Copies","We recommend you print or download this Disclosure, applicable service agreements, and other Communications for your permanent records."],
  ];

  return (
    <FormShell title="Electronic Communications Disclosure"
      subtitle="Please read this eCommunications Disclosure thoroughly — it contains important information about your legal rights."
      dbLabel="MongoDB · electronic_communications_disclosure"
      patientName={patientName} savedId={savedId} formRef={formRef}
      onSubmit={handleSubmit} onDownload={handleDownload} submitting={submitting}>

      {sections.map(([num, title, body]) => (
        <div className="wf-section" key={num}>
          <div className="wf-section-title"><span className="num">{num}</span>{title}</div>
          <div className="wf-policy"><p>{body}</p></div>
        </div>
      ))}

      <div className="wf-section">
        <div className="wf-section-title"><span className="num">✓</span>Acknowledgments</div>
        {[
          ["understoodRights",     "I have read and understand my legal rights regarding electronic communications."],
          ["understoodTypes",      "I understand the types of electronic communications I will receive."],
          ["understoodMinReqs",    "I confirm I have the hardware and software required to access electronic communications."],
          ["understoodWithdrawal", "I understand how to withdraw my consent to electronic communications."],
        ].map(([k,lbl])=>(
          <label key={k} style={{display:"flex",gap:12,cursor:"pointer",marginBottom:14,alignItems:"flex-start"}}>
            <input type="checkbox" style={{marginTop:3,accentColor:C.teal,flexShrink:0}}
              checked={form[k]} onChange={e=>set(k,e.target.checked)} />
            <span style={{fontSize:14,lineHeight:1.6}}>{lbl}</span>
          </label>
        ))}
        <Field label="Do you have the hardware and software to access electronic communications?">
          <div className="wf-radio-group">
            {["Yes","No"].map(o=>(
              <label className="wf-radio-label" key={o}>
                <input type="radio" name="hasHW" value={o} checked={form.hasHardwareSoftware===o}
                  onChange={e=>set("hasHardwareSoftware",e.target.value)} />{o}
              </label>
            ))}
          </div>
        </Field>
      </div>

      <SigBlock sigRef={sigRef} sigEmpty={sigEmpty} setSigEmpty={setSigEmpty}
        responsibleParty={form.responsibleParty} setResponsibleParty={v=>set("responsibleParty",v)}
        relationship={form.relationship} setRelationship={v=>set("relationship",v)}
        sigDate={form.sigDate} setSigDate={v=>set("sigDate",v)} />

      {Toast}
    </FormShell>
  );
}

/*
── Mongoose Schema ────────────────────────────────────────────────────────────
const ECommsSchema = new Schema({
  formType: { type:String, default:"electronic_communications_disclosure" },
  patient:  { name:String },
  acknowledgments: {
    hasHardwareSoftware:  String,
    understoodRights:     Boolean, understoodTypes:Boolean,
    understoodWithdrawal: Boolean, understoodMinReqs:Boolean,
  },
  authorization: { responsibleParty:String, relationship:String, sigDate:Date, signatureDataURL:String },
  meta: { submittedAt:Date, formVersion:String },
}, { timestamps:true, collection:"electronic_communications_disclosure" });
*/
